import Col from 'components/Col';
import Spinner2 from 'components/Spinner2';
import { Api, theme, getCurrencySign } from 'lib';
import { isAppSource, returnToApp } from 'core/returnToApp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Error, PaymentSection, PlanCard } from './components';
import { buildPlanCards } from './lib/buildPlanCards';
import { COLORS, FONT } from './lib/tokens';
import './inter.css';

const Screen = styled.div`
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(111.2deg, ${COLORS.bgFrom} 25%, ${COLORS.bgTo} 75%);
  color: ${COLORS.text};
  font-family: ${FONT};
  padding: 32px 16px 48px;
`;

const Column = styled.main`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Hero = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.cta};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e60023;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: ${COLORS.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.muted};
`;

const loginUrl = (target) => `/login?redirect=${encodeURIComponent(target)}`;

async function fetchPlans(teamId) {
  const params = new URLSearchParams({ teamId, surface: 'web' });
  const res = await Api.get(`/subscriptionPlans/byTeam?${params.toString()}`);
  return res.data;
}

const SubscriptionPlansScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const isLoggedIn = !!user;
  const teamId = new URLSearchParams(location.search).get('teamId');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState('');

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchPlans(teamId));
    } catch (err) {
      console.log('Error fetching subscription plans');
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // The backend returns null for guests; ignore anything else a guest somehow receives.
  const currentSubscription = isLoggedIn ? data?.currentSubscription || null : null;
  const currencySign = getCurrencySign(data?.currency) || '¥';

  const cards = useMemo(
    () => buildPlanCards(data?.plans, currentSubscription, { currencySign, t }),
    [data, currentSubscription, currencySign, t]
  );

  const selectCard = (card, interval = 'monthly') => {
    const params = new URLSearchParams({ planId: card.id, teamId, interval });
    const paymentUrl = `/payment-method-selection?${params.toString()}`;
    navigate(isLoggedIn ? paymentUrl : loginUrl(paymentUrl));
  };

  const continueFree = () => {
    if (isAppSource(location.search)) returnToApp(teamId);
    else navigate(-1);
  };

  const restore = async () => {
    if (!isLoggedIn) {
      navigate(loginUrl(`${location.pathname}${location.search}`));
      return;
    }
    setRestoring(true);
    setRestoreMessage('');
    try {
      const fresh = await fetchPlans(teamId);
      setData(fresh);
      setRestoreMessage(
        t(fresh?.currentSubscription ? 'subscription.paywall.restoreFound' : 'subscription.paywall.restoreNotFound')
      );
    } catch {
      setRestoreMessage(t('subscription.paywall.restoreError'));
    } finally {
      setRestoring(false);
    }
  };

  if (loading && !data) {
    return (
      <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
        <Spinner2 col={theme.SECONDARY} />
      </Col>
    );
  }

  if (error) {
    return <Error goBackFunction={() => navigate('/', { state: { fromAuth: true } })} retryFunction={loadPlans} />;
  }

  return (
    <Screen>
      <Column>
        <Hero>
          <Eyebrow>{t('subscription.paywall.eyebrow')}</Eyebrow>
          <Title>{t('subscription.paywall.title')}</Title>
          <Subtitle>{t('subscription.paywall.subtitle')}</Subtitle>
        </Hero>
        {cards.map((card) => (
          <PlanCard key={card.id} card={card} onSelect={selectCard} />
        ))}
        <PaymentSection
          onContinueFree={continueFree}
          onRestore={restore}
          restoring={restoring}
          restoreMessage={restoreMessage}
        />
      </Column>
    </Screen>
  );
};

export default SubscriptionPlansScreen;
